import { usePairs } from '@/store/pairs';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    ChartingLibraryWidgetOptions,
    LanguageCode,
    Overrides,
    ResolutionString,
    widget,
} from '../../../public/charting_library';
import { datafeed } from './datafeed';
import { FormValue } from '../trading/type';

export interface ChartContainerProps {
    symbol: ChartingLibraryWidgetOptions['symbol'];
    interval: ChartingLibraryWidgetOptions['interval'];

    // BEWARE: no trailing slash is expected in feed URL
    datafeedUrl: string;
    libraryPath: ChartingLibraryWidgetOptions['library_path'];
    chartsStorageUrl: ChartingLibraryWidgetOptions['charts_storage_url'];
    chartsStorageApiVersion: ChartingLibraryWidgetOptions['charts_storage_api_version'];
    clientId: ChartingLibraryWidgetOptions['client_id'];
    userId: ChartingLibraryWidgetOptions['user_id'];
    fullscreen: ChartingLibraryWidgetOptions['fullscreen'];
    autosize: ChartingLibraryWidgetOptions['autosize'];
    studiesOverrides: ChartingLibraryWidgetOptions['studies_overrides'];
    container: ChartingLibraryWidgetOptions['container'];
}

const getLanguageFromURL = (): LanguageCode | null => {
    const regex = new RegExp('[\\?&]lang=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null
        ? null
        : (decodeURIComponent(results[1].replace(/\+/g, ' ')) as LanguageCode);
};

const overrides: Overrides = {
    'paneProperties.backgroundGradientStartColor': '#000000',
    'paneProperties.backgroundGradientEndColor': '#000000',
    'mainSeriesProperties.candleStyle.upColor': '#15A74F',
    'mainSeriesProperties.candleStyle.downColor': '#D91644',
};

function Chart({ className = '' }: { className?: string }) {
    const chartContainerRef = useRef<HTMLDivElement>(null);

    const { control } = useFormContext<FormValue>();

    const pairIndex = useWatch({ control, name: 'pairIndex' });

    const symbol = useMemo(() => {
        const chartName = usePairs
            .getState()
            .data.find(
                (item) => item.pairIndex === Number(pairIndex)
            )?.chartName;

        if (!chartName) {
            return 'BTCUSDT';
        }

        return chartName.split(':')[1];
    }, [pairIndex]);

    useEffect(() => {
        if (!chartContainerRef.current) {
            return () => {};
        }

        const widgetOptions: ChartingLibraryWidgetOptions = {
            symbol: symbol,
            datafeed: datafeed,
            interval: '5' as ResolutionString,
            container: chartContainerRef.current,
            library_path: '/charting_library/',
            locale: getLanguageFromURL() || 'en',
            disabled_features: [
                'use_localstorage_for_settings',
                'header_symbol_search',
                'header_compare',
                'header_in_fullscreen_mode',
                'header_fullscreen_button',
                'left_toolbar',
                'timeframes_toolbar',
                // 'header_saveload',
                // "border_around_the_chart"
            ],
            enabled_features: [
                // 'move_logo_to_main_pane',
                'hide_resolution_in_legend',
                'confirm_overwrite_if_chart_layout_with_name_exists',
                'iframe_loading_compatibility_mode',
            ],

            charts_storage_url: 'https://saveload.tradingview.com',
            charts_storage_api_version: '1.1',
            client_id: 'tradingview.com',
            user_id: 'public_user_id',
            fullscreen: false,
            autosize: true,
            studies_overrides: {},
            theme: 'Light',
            // timezone: "Etc/UTC",
            timezone: 'Asia/Ho_Chi_Minh', //dayjs.tz.guess() as Timezone,
            debug: false,
            loading_screen: {
                backgroundColor: '#fff',
                foregroundColor: '#fff',
            },
            toolbar_bg: '#fff',
            // custom_font_family: "'Inter', sans-serif",
            // custom_css_url: '/charting_library/custom-styles.css',
            overrides,
        };

        const tvWidget = new widget(widgetOptions);

        tvWidget.onChartReady(() => {
            tvWidget.headerReady().then(() => {});
        });

        return () => {
            tvWidget.remove();
        };
    });

    useEffect(() => {
        const originalWindowOpen = window.open;

        window.open = function (url?: string | URL | undefined, ...args) {
            if (typeof url === 'string' && url.startsWith('blob:')) {
                return null;
            }
            return originalWindowOpen(url, ...args);
        };

        return () => {
            window.open = originalWindowOpen;
        };
    }, []);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a') as HTMLAnchorElement;

            if (anchor?.href?.startsWith('blob:')) {
                e.preventDefault();
            }
        };

        document.addEventListener('click', handler);

        return () => {
            document.removeEventListener('click', handler);
        };
    }, []);

    return (
        <div
            ref={chartContainerRef}
            className={clsx('', className)}
            style={{
                height: `calc(100dvh - 362px)`
            }}
        />
    );
}

export const ChartView = memo(Chart);

export const ChartPro = () => {
    return <Chart />;
};
